/* import __COLOCATED_TEMPLATE__ from './configure-plan.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  P5_CORE_PLAN_UPGRADE_FEATURES,
  PLAN_DATA,
  PRICING_5_X_CORE_ESSENTIAL_ID,
  PROACTIVE_SUPPORT_PLUS_BASE_ID,
} from 'embercom/lib/billing';
import type Plan from 'embercom/models/plan';
import type QuoteService from 'embercom/services/quote-service';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import type MarketingFeaturesService from 'embercom/services/marketing-features-service';

interface Args {
  corePlans: Array<Plan>;
  yearlyBillingPeriodSelected: boolean;
  selectedPlanId: string;
  seatNumber: number;
  minimumSeatNumber: number;
  selectedProactiveAddon: boolean;
  selectedCopilotAddon: boolean;
  selectedCopilotAdmins: Array<string>;
  copilotInitialised: boolean;
  hasSelectedPerformanceProAddon: boolean;
  fullSeatPrice: number;
  discountedSeatPrice: number;
  fullProactiveAddonPrice: number;
  perSeatCopilotAddonPrice: number;
  discountedCopilotSeatPrice: number;
  fullPerformanceProAddonPrice?: number;
  proactiveSupportBaseUsage: string;
  loading: boolean;
  updateSeatNumber: (seatNumber: number, didRemoveTeammates?: boolean) => void;
  updateSelectedPlan: (plan: Plan) => void;
  setSelectedProactiveAddon: (selectedProactiveAddon: boolean) => void;
  setCopilotAsInitialised: (selectedCopilotAddon: boolean) => void;
  setHasSelectedPerformanceProAddon: (hasSelectedPerformanceProAddon: boolean) => void;
  showProactiveSupportProArticle: () => void;
  isEarlyStageProgression: boolean;
  openCopilotModalOnLoad?: boolean;
}

interface Signature {
  Args: Args;
  Element: never;
}

export default class ConfigurePlan extends Component<Signature> {
  @service declare intl: any;
  @service declare quoteService: QuoteService;
  @service declare appService: AppService;
  @service declare intercomEventService: any;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare marketingFeaturesService: MarketingFeaturesService;

  @tracked isCopilotSelectorModalOpen = this.args.openCopilotModalOnLoad || false;
  @tracked isTeammateRemovalModalOpen = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.aiAgentSetupService.reloadWorkflowsCounts();
  }

  get seatInputLabelKey() {
    return 'signup.teams.pricing5.annual-plans.configure-plan.seat-input-label';
  }

  get selectedPlan() {
    return this.args.corePlans.find((plan) => plan.id === this.args.selectedPlanId);
  }

  get planFeatureKeys(): any {
    return P5_CORE_PLAN_UPGRADE_FEATURES[this.args.selectedPlanId];
  }

  get proactiveSupportPlusArticleId() {
    return 9061648; // https://www.intercom.com/help/articles/9061648
  }

  get proactiveSupportPlusFeatures(): Array<Array<{ text: string; tooltip?: string }>> {
    let categories = this.marketingFeaturesService.categories;
    let category = categories['proactive_support_plus'];

    if (!category?.subGroups) {
      return [[], []];
    }

    let allFeatures = this.marketingFeaturesService.getFeaturesByPlanId(
      Number(PROACTIVE_SUPPORT_PLUS_BASE_ID),
    );

    let billedSubgroup = category.subGroups['billed_after_500_messages_sent_monthly'];
    let unlimitedSubgroup = category.subGroups['unlimited_use'];

    let leftColumn = (billedSubgroup?.features ?? [])
      .map((featureId) => allFeatures[featureId])
      .filter(Boolean)
      .map((feature) => ({
        text: feature.title,
        tooltip: feature.description,
      }));

    let rightColumn = (unlimitedSubgroup?.features ?? [])
      .map((featureId) => allFeatures[featureId])
      .filter(Boolean)
      .map((feature) => ({
        text: feature.title,
        tooltip: feature.description,
      }));

    return [leftColumn, rightColumn];
  }

  get proactiveSupportPlusSubcopyList() {
    return [
      {
        text: this.intl.t(
          `signup.teams.pricing5.annual-plans.upgrades.proactive-support.subcopy-1`,
          { baseUsage: this.args.proactiveSupportBaseUsage },
        ),
      },
      {
        text: this.intl.t(
          `signup.teams.pricing5.annual-plans.upgrades.proactive-support.subcopy-2`,
        ),
      },
    ];
  }

  get copilotArticleId() {
    return 9121374; // https://www.intercom.com/help/articles/9121374
  }

  get seatNumber() {
    return this.args.seatNumber;
  }

  set seatNumber(value: number) {
    let seatNumber: number;
    if (value <= this.args.minimumSeatNumber) {
      seatNumber = this.args.minimumSeatNumber;
    } else if (value > 100) {
      seatNumber = 100;
    } else {
      seatNumber = value;
    }

    // If the user intended to reduce seats - invoke the teammate removal modal
    // The modal should come up when the user intends to reduce seats but there are seats to remove
    if (value < this.args.minimumSeatNumber && value > 0) {
      this.openTeammateRemovalModal();
    }

    this.args.updateSeatNumber(seatNumber);
  }

  get notLowestTierPlan() {
    let plan = this.args.corePlans.find((plan) => plan.id === this.args.selectedPlanId) as Plan;
    return plan.tierId > 1;
  }

  get isPlanWithLiteSeatsSelected() {
    return (PLAN_DATA[this.args.selectedPlanId].liteSeatLimit ?? 0) > 0;
  }

  get planList() {
    return this.args.corePlans.map((plan: Plan) => {
      return {
        text: plan.name,
        description: this.intl.t(PLAN_DATA[plan.id].checkoutDescriptionTranslationKey),
        value: plan.id,
      };
    });
  }

  get featuresLeftCol() {
    return this.planFeatureKeys.left;
  }

  get featuresRightCol() {
    return this.planFeatureKeys.right;
  }

  get seatPrice() {
    return this.args.yearlyBillingPeriodSelected
      ? this.args.discountedSeatPrice
      : this.args.fullSeatPrice;
  }

  get showCopilotAddonCard() {
    return this.args.isEarlyStageProgression
      ? this.args.selectedCopilotAdmins.length > 0
      : this.appService.app.copilotProduct;
  }

  get showPerformanceProAddonCard() {
    return this.appService.app.canSeeAiInsightsAddon;
  }

  get shouldSeatSelectorBeDisabled() {
    return this.args.isEarlyStageProgression;
  }

  get liteSeatNumber() {
    return this.appService.app.humanAdminsWithLiteSeat.length;
  }

  get shouldShowLiteSeatToFullSeatWarning() {
    return (
      this.args.selectedPlanId === PRICING_5_X_CORE_ESSENTIAL_ID &&
      this.appService.app.humanAdminsWithLiteSeat.length > 0
    );
  }

  get shouldShowPauseWorkflowsWarning() {
    return (
      this.args.selectedPlanId === PRICING_5_X_CORE_ESSENTIAL_ID &&
      (this.aiAgentSetupService.totalLiveWorkflowsCount > 0 ||
        this.aiAgentSetupService.totalPausedWorkflowsCount > 0)
    );
  }

  get seatNumberToAdminCount() {
    let hasAssignedCoreSeats = this.appService.app.humanAdminsWithCoreSeat.length;
    let viewingEssential = this.args.selectedPlanId === PRICING_5_X_CORE_ESSENTIAL_ID;

    return hasAssignedCoreSeats && !viewingEssential
      ? this.appService.app.humanAdminsWithCoreSeat.length
      : this.appService.app.humanAdmins.length;
  }

  @action updateSelectedPlan(planId: string) {
    // this will not be undefined but typescript isn't happy
    let plan = this.args.corePlans.find((plan) => plan.id === planId) as Plan;
    this.args.updateSelectedPlan(plan);
  }

  @action openTeammateRemovalModal() {
    this.isTeammateRemovalModalOpen = true;
  }

  @action onTeammateRemovalModalClose(didRemoveTeammates?: boolean) {
    this.isTeammateRemovalModalOpen = false;
    if (didRemoveTeammates) {
      // After teammates are removed, reset seat number to the new minimum (humanAdmins.length)
      this.args.updateSeatNumber(this.seatNumberToAdminCount, true);
    }
  }

  @action toggleCopilotSelectorModal() {
    this.isCopilotSelectorModalOpen = !this.isCopilotSelectorModalOpen;
  }

  @action confirmCopilotSelection() {
    this.toggleCopilotSelectorModal();
    this.args.setCopilotAsInitialised(true);
  }

  @action toggleEsProgressionCopilotSelection() {
    this.args.setCopilotAsInitialised(!this.args.selectedCopilotAddon);
  }

  @action toggleAddonSelectedState() {
    if (this.args.isEarlyStageProgression) {
      this.toggleEsProgressionCopilotSelection();
    } else {
      this.toggleCopilotSelectorModal();
    }
  }

  @action togglePerformanceProAddon() {
    this.args.setHasSelectedPerformanceProAddon(!this.args.hasSelectedPerformanceProAddon);
  }

  @action showSeatsArticle(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'learn_more_about_seats_article',
      place: 'p5-checkout-convert-to-paid',
    });
    window.Intercom('showArticle', 8205716); // https://www.intercom.com/help/en/articles/8205716-seats
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Signup::Teams::Pricing5::Components::ConfigurePlan': typeof ConfigurePlan;
  }
}
